/**
 * 74.11 (a) (DECISIONS 373; ARCHITECTURE §5.48): what a front carries — with the weather cells on, a cell's kind is drawn
 * from the climate cell under its birth place (core/climate.ts CLIMATE_WEATHER), the hour at its birth (74.2's clock in
 * core: skyClock.ts matchHour) weighting the draw, the air's density gating it; the draws are the timeline's keyed ones
 * (weather.ts weatherAtMix), so a seed's rhythm is kept. Pure; nothing here with the cells off (the timeline's kinds).
 */
import type { GameState, PlanetState, Vec3 } from '../types';
import { WEATHER_SEGMENT_S, weatherAtMix, type WeatherKind } from './weather';
import { frontMix } from './climate';
import { airOf } from './air';
import { matchHour } from './skyClock';
import { memoGroundKey } from './spinAxis';

type FrontState = Pick<GameState, 'config' | 'planets'>;

/**
 * 382: the frequency control — the weather kinds' odds in a front's draw × `frequency`, clear's kept (1: the mix as the climate,
 * the hour and the air give it; 2: weather twice as likely against clear; 0.5: half). A mix with no weather kind (an airless world,
 * the air's gate) is unchanged: dry and airless worlds stay as their climate says. Pure.
 */
export function withFrequency(mix: Partial<Record<WeatherKind, number>>, frequency: number): Partial<Record<WeatherKind, number>> {
  if (frequency === 1) return mix;
  const out: Partial<Record<WeatherKind, number>> = {};
  for (const k of Object.keys(mix) as WeatherKind[]) out[k] = k === 'clear' ? mix[k] : (mix[k] ?? 0) * frequency;
  return out;
}

/**
 * 384 (lane F's 25-a-side sweep): the mix key was built per call — 54 MB of strings over a run; it is built once per config and
 * planet object now and kept in a WeakMap, the same string: every input is a field of the two objects, which are replaced,
 * never edited, when a world or a match changes (a crater is a new planet object; the Lab builds a new match); 382's frequency is a
 * config field, so it is in the key too.
 */
const mixKeys = new WeakMap<object, WeakMap<object, string>>();
function mixKeyOf(state: FrontState, planet: PlanetState, density: number, segmentS: number, frequency: number): string {
  let byPlanet = mixKeys.get(state.config);
  if (!byPlanet) { byPlanet = new WeakMap(); mixKeys.set(state.config, byPlanet); }
  let k = byPlanet.get(planet);
  if (k === undefined) { k = `front|${memoGroundKey(planet)}|${planet.radius}|${planet.archetype}|${density}|${state.config.skyHour ?? 's'}|${state.config.skyRate ?? 'd'}|${JSON.stringify(state.config.skyWeights ?? null)}|${segmentS}|${frequency}`; byPlanet.set(planet, k); }
  return k;
}

/** The kind of the front on track `trackKey` in `segment`: `birthPlaceOf(s)` is where segment s's front is born (its keyed place at its segment's middle). */
export function frontKind(state: FrontState, planet: PlanetState, trackKey: string, segment: number, birthPlaceOf: (s: number) => Vec3): WeatherKind {
  const segmentS = state.config.clock === 'action' ? (state.config.weatherSegmentS ?? WEATHER_SEGMENT_S) : WEATHER_SEGMENT_S;
  const density = airOf(planet).density;
  // (fix/74.11-memo-keys, lane F's find: the timeline memo is keyed on this and the seed-and-planet-id key — two matches in one
  // process with the same seed and planet id but another world (another size, another ground) must not share a timeline, so
  // every input of frontMix and matchHour is here: the generated ground (the climate at the birth place; 74.19: the carved ground, memoGroundKey), the radius (the
  // air's density, the hour's system by size), the archetype, the air, the sky's pins and weights)
  const frequency = state.config.weatherFrequency ?? 1;
  const mixKey = mixKeyOf(state, planet, density, segmentS, frequency);
  return weatherAtMix(trackKey, mixKey, s => withFrequency(frontMix(planet, birthPlaceOf(s), matchHour(state.config, planet, (s + 0.5) * segmentS), density), frequency), segment);
}
